// Turns a parsed BSP into Three.js meshes. The geometry (mapGeometry.ts) is
// batched per (shader, lightmap atlas page, fog) and can be built in the map
// loading worker; here it becomes buffers, shader materials and fog passes.
// World batches keep the index range of every surface, so the PVS
// (R_MarkLeaves) can rebuild their index buffers with only the potentially
// visible surfaces. The scene stays in Quake coordinates (Z up).

import * as THREE from 'three';
import { clusterVisible, pointInLeaf, type Bsp } from '../shared/bsp';
import type { Entity } from '../shared/entities';
import type { VirtualFS } from '../fs/vfs';
import { LIGHTMAP_BY_VERTEX, LIGHTMAP_NONE, createShaderMaterials, createSkyMaterial, resolveShader, type MaterialContext, type SharedUniforms } from './q3shader';
import type { ShaderDef } from './shaderScripts';
import { SkyBox } from './sky';
import { EntityRenderKit } from './entityParts';
import { MapModels } from './mapModels';
import { createFogImage, createFogMaterial, loadFogs, type MapFogs } from './fog';
import type { GeometryBatch } from './mapGeometry';
import { prepareScene, type PreparedScene } from './scenePrep';

export { prepareScene, surfaceFog, type PreparedScene } from './scenePrep';
import type { TextureManager } from './textures';

/** A world batch whose index buffer follows the PVS. */
interface WorldBatch {
  geo: THREE.BufferGeometry;
  meshes: THREE.Mesh[];
  full: Uint32Array;
  visible: Uint32Array;
  ranges: Int32Array;
  bounds: Float32Array;
}

export interface MapSceneOptions {
  bsp: Bsp;
  mapName: string;
  entities: Entity[];
  vfs: VirtualFS;
  textures: TextureManager;
  shaders: Map<string, ShaderDef>;
  prepared?: PreparedScene;
  /** GL_MAX_TEXTURE_SIZE (atlas pages are at most 4096). */
  maxTextureSize?: number;
  /** Low graphics: one cloud layer in the sky. */
  simpleSky?: boolean;
}

const dataTexture = (data: Uint8Array, w: number, h: number): THREE.DataTexture => {
  const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat);
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.flipY = false;
  tex.needsUpdate = true;
  return tex;
};

export class MapScene {
  readonly root = new THREE.Group();
  readonly shared: SharedUniforms = { uTime: { value: 0 }, uBrightness: { value: 1 } };
  sky: SkyBox | null = null;
  private skySurfaces = 0;
  /** Atlas pages, or the single lightmaps (no atlas). */
  private pages: THREE.Texture[] = [];
  private lightmaps: (THREE.Texture | null)[] = [];
  private disposables: { dispose(): void }[] = [];
  private worldBatches: WorldBatch[] = [];
  /** Per-frame shader work (noise waves, animMap frames). */
  private animators: ((time: number) => void)[] = [];
  /** Cluster the visible index buffers were built for (undefined = not built yet). */
  private viewCluster: number | undefined = undefined;
  private visCount = 0;
  private surfVisCount: Int32Array;
  /** Root group of each drawn brush entity, by entity number. */
  readonly entityGroups = new Map<number, THREE.Group>();
  /** misc_gamemodels. */
  models: MapModels | null = null;
  /** Fog volumes and the global fog. */
  readonly fogs: MapFogs;
  private fogImage: THREE.Texture | null = null;
  private readonly fogMaterials = new Map<number, THREE.RawShaderMaterial | null>();
  /** Batches drawn (statistics). */
  batchCount = 0;

  private constructor(private readonly opts: MapSceneOptions) {
    this.surfVisCount = new Int32Array(opts.bsp.surfaces.length);
    this.fogs = loadFogs(opts.bsp, opts.shaders, opts.entities.find((e) => e.classname === 'worldspawn'));
  }

  /** The fog pass material of a fog (one per fog volume). */
  private fogMaterial(fogNum: number): THREE.RawShaderMaterial | null {
    if (fogNum === -1) return null;
    let mat = this.fogMaterials.get(fogNum);
    if (mat === undefined) {
      const fog = fogNum === -2 ? this.fogs.global : this.fogs.volumes[fogNum];
      this.fogImage ??= createFogImage();
      mat = fog ? createFogMaterial(fog, this.fogImage, this.shared) : null;
      if (mat) this.disposables.push(mat);
      this.fogMaterials.set(fogNum, mat);
    }
    return mat;
  }

  static async build(opts: MapSceneOptions): Promise<MapScene> {
    const scene = new MapScene(opts);
    const prepared = opts.prepared ?? (await prepareScene(opts.bsp, opts.mapName, opts.entities, opts.shaders, (p) => opts.vfs.read(p), opts.maxTextureSize));
    if (prepared.atlas) {
      const size = prepared.atlas.pageSize;
      scene.pages = prepared.atlas.pages.map((data) => dataTexture(data, size, size));
      scene.disposables.push(...scene.pages);
    } else if (prepared.lightmaps) {
      scene.lightmaps = prepared.lightmaps.map((img) => (img ? dataTexture(img.data, img.width, img.height) : null));
      for (const t of scene.lightmaps) if (t) scene.disposables.push(t);
    }
    scene.skySurfaces = prepared.geometry.skySurfaces;
    for (const m of prepared.geometry.models) {
      const group = new THREE.Group();
      if (m.origin) group.position.set(m.origin[0], m.origin[1], m.origin[2]);
      for (const batch of m.batches) {
        const geo = scene.createGeometry(batch, m.model === 0);
        const meshes = scene.createMeshes(batch, geo);
        if (meshes.length) group.add(...meshes);
        scene.batchCount++;
        if (m.model === 0) {
          const visible = geo.index!.array as Uint32Array;
          scene.worldBatches.push({ geo, meshes, full: visible.slice(), visible, ranges: batch.ranges, bounds: batch.bounds });
        }
      }
      scene.root.add(group);
      if (m.entityNum >= 0) scene.entityGroups.set(m.entityNum, group);
    }
    await scene.buildSky();
    scene.models = await MapModels.load(opts.entities, opts.vfs, new EntityRenderKit(opts.shaders, opts.textures, scene.shared, opts.bsp), opts.bsp);
    scene.root.add(scene.models.object);
    return scene;
  }

  private createGeometry(batch: GeometryBatch, world: boolean): THREE.BufferGeometry {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(batch.pos, 3));
    if (!batch.sky) {
      geo.setAttribute('uv', new THREE.BufferAttribute(batch.st, 2));
      geo.setAttribute('lmuv', new THREE.BufferAttribute(batch.lm, 2));
      geo.setAttribute('normal', new THREE.BufferAttribute(batch.normal, 3));
      geo.setAttribute('color', new THREE.BufferAttribute(batch.color, 4));
    }
    // world batches always use 32 bit indices: the PVS rewrites their buffer
    if (world || batch.pos.length / 3 > 65535) geo.setIndex(new THREE.BufferAttribute(batch.index, 1));
    else geo.setIndex(new THREE.Uint16BufferAttribute(batch.index, 1));
    geo.computeBoundingSphere();
    this.disposables.push(geo);
    return geo;
  }

  /**
   * Meshes of a batch: one per pass of its shader (usually one), sharing the
   * geometry. renderOrder follows ET's sort key, then the shader, then the pass;
   * blended passes are not depth sorted (see Game's transparent sort).
   */
  private createMeshes(batch: GeometryBatch, geo: THREE.BufferGeometry): THREE.Mesh[] {
    const { bsp, textures } = this.opts;
    const name = bsp.shaders[batch.shaderNum]?.name ?? 'noshader';
    const def = this.opts.shaders.get(name);
    const lightmap = batch.page >= 0 ? (this.pages[batch.page] ?? null) : batch.lightmapNum >= 0 ? (this.lightmaps[batch.lightmapNum] ?? null) : null;
    // R_FindLightmap: a missing (external) lightmap makes the surface vertex lit
    const lightmapIndex = batch.lightmapNum >= 0 ? (lightmap ? batch.lightmapNum : LIGHTMAP_BY_VERTEX) : batch.lightmapNum === LIGHTMAP_NONE ? LIGHTMAP_NONE : LIGHTMAP_BY_VERTEX;
    const shader = resolveShader(name, def, lightmapIndex);
    const ctx: MaterialContext = { textures, shared: this.shared, lightmap, animators: this.animators, simpleSky: this.opts.simpleSky };
    const materials = batch.sky ? [createSkyMaterial(shader, ctx)] : createShaderMaterials(shader, ctx);
    const meshes = materials.map((mat, pass) => {
      this.disposables.push(mat);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.name = name;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      mesh.renderOrder = shader.sort * 1e6 + batch.shaderNum * 16 + pass;
      return mesh;
    });
    // RB_FogPass: opaque shaders (the first stage writes depth) get the fog blended over them
    const fog = !batch.sky && materials[0]?.depthWrite ? this.fogMaterial(batch.fogNum) : null;
    if (fog) {
      const mesh = new THREE.Mesh(geo, fog);
      mesh.name = `${name} (fog)`;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      mesh.renderOrder = shader.sort * 1e6 + batch.shaderNum * 16 + 15;
      meshes.push(mesh);
    }
    return meshes;
  }

  private async buildSky(): Promise<void> {
    const { bsp, shaders, textures } = this.opts;
    // with sky surfaces the sky is drawn by them; the global box is only for unsealed maps without any
    if (this.skySurfaces) return;
    let skyName: string | null = null;
    for (const s of bsp.shaders) {
      const def = shaders.get(s.name);
      if (def?.sky) {
        skyName = def.sky.farbox;
        break;
      }
    }
    this.sky = await SkyBox.create(skyName, textures);
    if (this.sky) this.disposables.push(this.sky);
  }

  /** Advances shader time (seconds): waveforms, noise uniforms, animMap frames. */
  update(time: number): void {
    this.shared.uTime.value = time;
    for (const a of this.animators) a(time);
    this.models?.update(time);
    this.models?.kit.update(time);
  }

  /**
   * R_MarkLeaves: when the camera enters another cluster, rebuild the index
   * buffers of the world batches from the surfaces of the leaves in its PVS
   * (deduplicated with a visit counter, like R_AddWorldSurface's viewCount) and
   * their bounding spheres, so Three's frustum culling works per batch. Area
   * portals are ignored (all areas open). Inline models are culled by bounds only.
   */
  updateVisibility(cameraPos: ArrayLike<number>): void {
    const { bsp } = this.opts;
    const leaf = bsp.leafs[pointInLeaf(bsp, cameraPos)];
    const cluster = leaf ? leaf.cluster : -1;
    if (cluster === this.viewCluster) return;
    this.viewCluster = cluster;
    this.models?.updateVisibility(cluster);
    const vis = bsp.visibility;
    const all = !vis || cluster < 0 || cluster >= vis.numClusters;
    const count = ++this.visCount;
    if (!all) {
      for (const l of bsp.leafs) {
        if (!clusterVisible(vis, cluster, l.cluster)) continue;
        for (let i = 0; i < l.numLeafSurfaces; i++) this.surfVisCount[bsp.leafSurfaces[l.firstLeafSurface + i]] = count;
      }
    }
    const box = new THREE.Box3();
    for (const wb of this.worldBatches) {
      let n = 0;
      box.makeEmpty();
      for (let r = 0, b = 0; r < wb.ranges.length; r += 3, b += 6) {
        if (!all && this.surfVisCount[wb.ranges[r]] !== count) continue;
        const start = wb.ranges[r + 1];
        const len = wb.ranges[r + 2];
        wb.visible.set(wb.full.subarray(start, start + len), n);
        n += len;
        const bd = wb.bounds;
        box.expandByPoint(_v.set(bd[b], bd[b + 1], bd[b + 2]));
        box.expandByPoint(_v.set(bd[b + 3], bd[b + 4], bd[b + 5]));
      }
      const index = wb.geo.index!;
      index.clearUpdateRanges();
      index.addUpdateRange(0, n);
      index.needsUpdate = true;
      wb.geo.setDrawRange(0, n);
      if (n) box.getBoundingSphere(wb.geo.boundingSphere ?? (wb.geo.boundingSphere = new THREE.Sphere()));
      for (const m of wb.meshes) m.visible = n > 0;
    }
  }

  /** Cluster of the last updateVisibility call (-1 = outside the map / no vis). */
  get cluster(): number {
    return this.viewCluster ?? -1;
  }

  /** Length of the world model's bounds diagonal (sizes the far clip plane). */
  get worldSize(): number {
    const m = this.opts.bsp.models[0];
    return m ? Math.hypot(m.maxs[0] - m.mins[0], m.maxs[1] - m.mins[1], m.maxs[2] - m.mins[2]) : 0;
  }

  dispose(): void {
    for (const d of this.disposables) d.dispose();
    this.disposables = [];
    this.models?.dispose();
    this.models = null;
    this.fogImage?.dispose();
    this.fogImage = null;
  }
}

const _v = new THREE.Vector3();
